import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { mapInternalStateToPayload as mapJobNameAssignmentStateToPayload } from 'components/modals/common/JobNameAssignmentModal/actions'
import { mapInternalStateToPayload as mapJobPositionStateToPayload } from 'components/modals/common/JobPositionModal/actions'
import { useDeleteJobsModal } from 'components/modals/DeleteJobsModal/hooks'
import { useArchiveJobsModal } from 'components/modals/ArchiveJobsModal/hooks'
import { useExportJobsModal } from 'components/modals/ExportJobsModal/hooks'
import { useRole } from 'components/auth/features'
import { DEFAULT_JOBS_QUERY_PARAMS } from 'constants/projects'
import { getLocation } from 'ducks/router'
import { useModal, useSearch, useSnackbar } from 'hooks'
import { getOrm, jobTemplates, jobs, interviewTemplates } from 'orm'
import type { MutationCallbacks } from 'orm/types'
import { fetchDepartments } from 'api/departments'
import { NB_CODE } from 'utils/languages'
import { t } from 'ttag'
import {
  ACTION_DELETE,
  ACTION_ARCHIVE,
  ACTION_EXPORT,
  getActions,
  getFiltersConfigs,
} from './common/JobsTable/model'
import { getFilterInitState, getDepartmentValueFromQuery } from '../helpers'
import { INTERVIEW_MOUDLE_FIELD } from 'components/modals/common/JobPositionModal/tabs/Modules/model'

const useMutations = () => {
  const dispatch = useDispatch()

  const handlePostJobPosition = React.useCallback(
    (
      { data },
      {
        onFailureAction,
        onSuccessAction,
        postSuccessAction,
      }: MutationCallbacks = {},
    ) => {
      dispatch(
        jobs.postStart({
          data,
          onFailureAction,
          onSuccessAction,
          postSuccessAction,
        }),
      )
    },
    [dispatch],
  )

  return {
    handlePostJobPosition,
  }
}

const useData = () => {
  const { projects } = useSelector(getOrm)
  const templates = useSelector(jobTemplates.getItems)

  const [activeTemplate, setActiveTemplate] = React.useState<any>(null)
  const [project, setProject] = React.useState(null)

  const hasAnyTemplates = Object.keys(templates).length > 0

  return {
    state: { activeTemplate, project, projects, templates, hasAnyTemplates },
    handlers: { setActiveTemplate, setProject },
  }
}

export const useFilter = () => {
  const { query } = useSelector(getLocation)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filter, setFilter] = React.useState<Record<string, any>>(
    getFiltersConfigs().reduce(
      (acc, conf) =>
        getFilterInitState({ department: query.department })(acc, conf),
      {},
    ),
  )

  const [departmentValue, setDepartmentValue] = React.useState([])

  React.useEffect(() => {
    const loadDepartments = async () => {
      const { response } = await fetchDepartments({})
      setDepartmentValue(getDepartmentValueFromQuery(query, response?.results))
    }

    loadDepartments()
  }, [])

  const resetFilter = () => {
    const initFilter = getFilterInitState()
    setFilter(
      getFiltersConfigs().reduce((acc, conf) => initFilter(acc, conf), {}),
    )
    setDepartmentValue([])
  }

  const setDepartment = department => {
    setFilter({
      ...filter,
      department: department.map(dep => dep.value),
    })

    setDepartmentValue(department)
  }

  return {
    state: { filter, departmentValue },
    handlers: {
      setFilter,
      resetFilter,
      setDepartment,
    },
  }
}

const useActions = (state, handlers) => {
  const { projects, hasAnyTemplates, filter } = state
  const {
    handleDeleteJobsModalOpen,
    handleArchiveJobsModalOpen,
    handleExportJobsModalOpen,
  } = handlers

  const selectedCount = Object.keys(projects?.meta?.selected ?? {}).length

  const jobActions = getActions({ count: selectedCount, filter })

  const actions = React.useMemo(
    () =>
      jobActions.map(action => {
        const mapTypeToOnClick = {
          [ACTION_EXPORT]: handleExportJobsModalOpen,
          [ACTION_ARCHIVE]: handleArchiveJobsModalOpen,
          [ACTION_DELETE]: handleDeleteJobsModalOpen,
        }

        return {
          ...action,
          onClick: mapTypeToOnClick[action.type],
          isDisabled: selectedCount === 0,
        }
      }),
    [
      jobActions,
      selectedCount,
      handleDeleteJobsModalOpen,
      handleArchiveJobsModalOpen,
      handleExportJobsModalOpen,
    ],
  )

  return {
    state: { actions, hasAnyTemplates },
  }
}

const useSelectionModal = () => {
  const { isOpen, handleClose, handleOpen } = useModal()

  return {
    handleSelectionModalClose: handleClose,
    handleSelectionModalOpen: handleOpen,
    isSelectionModalOpen: isOpen,
  }
}

export const getDefaultTemplate = (data, templatePosition) => {
  const skills = data.competencies.reduce(
    (acc, item) => ({ ...acc, [item.name]: item[item.name] }),
    {},
  )

  return {
    department: data.department.value,
    language: NB_CODE,
    skills: skills,
    title: `${templatePosition} ${t`Template`}`,
  }
}

const useCreatePositionModal = handlers => {
  const { query } = useSelector(getLocation)
  const isAccountAdmin = useRole('accountAdmin')
  const dispatch = useDispatch()

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const { handleSaveTemplateModalOpen, setProject } = handlers

  const { isOpen, handleClose, handleOpen } = useModal()

  const handleConfirm = React.useCallback(
    async (data, { onError, onSuccess }) => {
      const payload = mapJobPositionStateToPayload(data)

      const templateValue = data?.template?.value
      const templatePosition = data?.position
      const hasInterviewModule = !!data?.modules.find(
        module => module.name === INTERVIEW_MOUDLE_FIELD,
      )[INTERVIEW_MOUDLE_FIELD]

      const postJob = (extendedPayload?: { interview_template: string }) => {
        dispatch(
          jobs.postStart({
            data: { ...payload, ...extendedPayload },
            noSnackbar: true,
            onFailureAction: onError,
            onSuccessAction: jobs.fetchStart(
              query ?? DEFAULT_JOBS_QUERY_PARAMS,
            ),
            postSuccessAction: () => {
              handleClose()
              setProject(payload)
              onSuccess()

              handleShowSnackbar({
                message: extendedPayload
                  ? t`Job and interview template were successfully created.`
                  : t`Job template was successfully created.`,
                type: 'success',
              })

              if (isAccountAdmin) {
                handleSaveTemplateModalOpen()
              }
            },
          }),
        )
      }

      // if default template was selected by the user
      // then create it and afterwards create a job
      if (hasInterviewModule && !templateValue) {
        dispatch(
          interviewTemplates.postStart({
            data: getDefaultTemplate(data, templatePosition),
            noSnackbar: true,
            postSuccessAction: () =>
              postJob({
                interview_template: `${templatePosition} ${t`Template`}`,
              }),
          }),
        )
      } else {
        postJob()
      }
    },
    [
      query,
      dispatch,
      handleClose,
      handleSaveTemplateModalOpen,
      setProject,
      isAccountAdmin,
      handleShowSnackbar,
    ],
  )

  return {
    handleCreatePositionModalClose: handleClose,
    handleCreatePositionModalConfirm: handleConfirm,
    handleCreatePositionModalOpen: handleOpen,
    isCreatePositionModalOpen: isOpen,
  }
}

const useSavePositionTemplateModal = () => {
  const dispatch = useDispatch()

  const { isOpen, handleClose, handleOpen } = useModal()

  const handleConfirm = React.useCallback(
    (
      data,
      {
        onFailureAction,
        onSuccessAction,
      }: Pick<MutationCallbacks, 'onFailureAction' | 'onSuccessAction'> = {},
    ) => {
      dispatch(
        jobTemplates.postStart({
          data,
          message: t`Job template was successfully created.`,
          onFailureAction,
          onSuccessAction,
          postSuccessAction: handleClose,
        }),
      )
    },
    [dispatch, handleClose],
  )

  return {
    handleSaveTemplateModalClose: handleClose,
    handleSaveTemplateModalConfirm: handleConfirm,
    handleSaveTemplateModalOpen: handleOpen,
    isSaveTemplateModalOpen: isOpen,
  }
}

const usePositionTemplateModal = handlers => {
  const { handleCreateTemplateModalOpen } = handlers

  const { isOpen, handleClose, handleOpen } = useModal()

  const handleConfirm = React.useCallback(() => {
    handleClose()
    handleCreateTemplateModalOpen()
  }, [handleClose, handleCreateTemplateModalOpen])

  return {
    handlePositionTemplateModalClose: handleClose,
    handlePositionTemplateModalConfirm: handleConfirm,
    handlePositionTemplateModalOpen: handleOpen,
    isPositionTemplateModalOpen: isOpen,
  }
}

const useCreatePositionTemplateModal = (state, handlers) => {
  const { activeTemplate } = state
  const { handlePostJobPosition } = handlers

  const { query } = useSelector(getLocation)

  const { isOpen, handleClose, handleOpen } = useModal()

  const handleConfirm = React.useCallback(
    ({ title }, { onFailureAction }) => {
      const data = mapJobNameAssignmentStateToPayload({
        ...activeTemplate,
        title,
      })

      handlePostJobPosition(
        { data },
        {
          onFailureAction,
          onSuccessAction: jobs.fetchStart(query ?? DEFAULT_JOBS_QUERY_PARAMS),
          postSuccessAction: handleClose,
        },
      )
    },
    [activeTemplate, query, handleClose, handlePostJobPosition],
  )

  return {
    handleCreateTemplateModalClose: handleClose,
    handleCreateTemplateModalConfirm: handleConfirm,
    handleCreateTemplateModalOpen: handleOpen,
    isCreateTemplateModalOpen: isOpen,
  }
}

export const useProjects = () => {
  const [search, setSearch] = useSearch()

  const { handlePostJobPosition } = useMutations()

  const {
    state: { activeTemplate, project, projects, hasAnyTemplates },
    handlers: { setActiveTemplate, setProject },
  } = useData()

  const {
    state: { filter, departmentValue },
    handlers: { setFilter, setDepartment, resetFilter },
  } = useFilter()

  const {
    handleSelectionModalClose,
    handleSelectionModalOpen,
    isSelectionModalOpen,
  } = useSelectionModal()

  const {
    handleSaveTemplateModalClose,
    handleSaveTemplateModalConfirm,
    handleSaveTemplateModalOpen,
    isSaveTemplateModalOpen,
  } = useSavePositionTemplateModal()

  const {
    handleCreatePositionModalClose,
    handleCreatePositionModalConfirm,
    handleCreatePositionModalOpen,
    isCreatePositionModalOpen,
  } = useCreatePositionModal({
    handleSaveTemplateModalOpen,
    setProject,
  })

  const {
    handleCreateTemplateModalClose,
    handleCreateTemplateModalConfirm,
    handleCreateTemplateModalOpen,
    isCreateTemplateModalOpen,
  } = useCreatePositionTemplateModal(
    { activeTemplate },
    { handlePostJobPosition },
  )

  const {
    handlePositionTemplateModalClose,
    handlePositionTemplateModalConfirm,
    handlePositionTemplateModalOpen,
    isPositionTemplateModalOpen,
  } = usePositionTemplateModal({ handleCreateTemplateModalOpen })

  const {
    handleExportJobsModalClose,
    handleExportJobsModalOpen,
    isExportJobsModalOpen,
  } = useExportJobsModal()

  const {
    handleArchiveJobsModalClose,
    handleArchiveJobsModalConfirm,
    handleArchiveJobsModalOpen,
    isArchiveJobsModalOpen,
  } = useArchiveJobsModal({ archived: filter.archived })

  const {
    handleDeleteJobsModalClose,
    handleDeleteJobsModalConfirm,
    handleDeleteJobsModalOpen,
    isDeleteJobsModalOpen,
  } = useDeleteJobsModal()

  const {
    state: { actions },
  } = useActions(
    { projects, hasAnyTemplates, filter },
    {
      handleDeleteJobsModalOpen,
      handleArchiveJobsModalOpen,
      handleExportJobsModalOpen,
    },
  )

  const handleCreatePositionModalBack = React.useCallback(() => {
    handleCreatePositionModalClose()
    handleSelectionModalOpen()
  }, [handleCreatePositionModalClose, handleSelectionModalOpen])

  const handleCreateTemplateModalBack = React.useCallback(() => {
    handleCreateTemplateModalClose()
    handlePositionTemplateModalOpen()
  }, [handleCreateTemplateModalClose, handlePositionTemplateModalOpen])

  const handlePositionTemplateModalBack = React.useCallback(() => {
    handlePositionTemplateModalClose()
    handleSelectionModalOpen()
  }, [handlePositionTemplateModalClose, handleSelectionModalOpen])

  const handleCreateTemplateOpen = React.useCallback(() => {
    handleSelectionModalClose()
    handlePositionTemplateModalOpen()
  }, [handlePositionTemplateModalOpen, handleSelectionModalClose])

  const handleCreatePositionOpen = React.useCallback(() => {
    handleSelectionModalClose()
    handleCreatePositionModalOpen()
  }, [handleCreatePositionModalOpen, handleSelectionModalClose])

  jobTemplates.useModel()

  return {
    state: {
      actions,
      activeTemplate,
      project,
      search,
      filter,
      departmentValue,
      hasAnyTemplates,
      isCreatePositionModalOpen,
      isCreateTemplateModalOpen,
      isDeleteJobsModalOpen,
      isArchiveJobsModalOpen,
      isExportJobsModalOpen,
      isPositionTemplateModalOpen,
      isSaveTemplateModalOpen,
      isSelectionModalOpen,
    },
    handlers: {
      handleCreatePositionOpen,
      handleCreateTemplateOpen,
      handleCreatePositionModalBack,
      handleCreatePositionModalClose,
      handleCreatePositionModalConfirm,
      handleCreateTemplateModalBack,
      handleCreateTemplateModalClose,
      handleCreateTemplateModalConfirm,
      handleDeleteJobsModalClose,
      handleDeleteJobsModalConfirm,
      handleArchiveJobsModalClose,
      handleArchiveJobsModalConfirm,
      handleExportJobsModalClose,
      handlePositionTemplateModalBack,
      handlePositionTemplateModalClose,
      handlePositionTemplateModalConfirm,
      handleSaveTemplateModalClose,
      handleSaveTemplateModalConfirm,
      handleSelectionModalClose,
      handleSelectionModalOpen,
      setActiveTemplate,
      setDepartment,
      setSearch,
      setFilter,
      resetFilter,
    },
  }
}
